<template>
  <div class="rentManage">
    <div class="content-container">
      <div class="titieMian">
        <div class="title-container">租金管理</div>
        <div class="select-container">
          <div v-if="isLevel2" class="select-container-box">
            <el-select
              v-model="filters.lessorEnterpriseId"
              size="small"
              clearable
              placeholder="请选择出租方"
            >
              <el-option
                v-for="item in companyOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
          <div class="select-container-box">
            <el-input
              v-model="filters.tenantName"
              size="small"
              clearable
              class="search-input"
              placeholder="请输入承租方名称"
              @keyup.enter.native="search(1)"
            />
          </div>
          <div v-if="isLevel2" class="select-container-box">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="支付开始日期"
              end-placeholder="支付结束日期"
              style="width: 280px"
            />
          </div>
          <div class="select-container-box">
            <el-button size="small" type="info" plain class="query" @click="search(1)">查询</el-button>
            <el-button size="small" type="info" plain @click="resetSearch">重置</el-button>
            <el-button v-if="isLevel2" size="small" type="success" @click="onExport">导出</el-button>
          </div>
        </div>
      </div>

      <div v-if="isLevel2" class="stats-cards" v-loading="statsLoading">
        <div class="stat-card">
          <div class="stat-label">资金收入</div>
          <div class="stat-value">{{ formatMoney(stats.incomeTotal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">已收金额</div>
          <div class="stat-value">{{ formatMoney(stats.receivedTotal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">待收金额</div>
          <div class="stat-value">{{ formatMoney(stats.pendingTotal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">平均单笔金额</div>
          <div class="stat-value">{{ formatMoney(stats.avgAmount) }}</div>
        </div>
      </div>

      <div class="table-container">
        <el-table :data="list" v-loading="loading" style="width:100%" size="medium">
          <i slot="empty" style="font-style:normal">暂无数据</i>
          <el-table-column label="序号" type="index" align="center" width="60" />
          <el-table-column
            v-if="isLevel2"
            prop="lessorName"
            label="出租方"
            align="center"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column
            prop="tenantName"
            label="承租方"
            align="center"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column label="支付日期" align="center" width="180">
            <template slot-scope="scope">{{ formatDateTime(scope.row.payDate) }}</template>
          </el-table-column>
          <el-table-column prop="payAmount" label="支付金额(元)" align="center" width="130" />
          <el-table-column label="支付方式" align="center" width="110">
            <template slot-scope="scope">{{ enumLabel("payMethod", scope.row.payMethod) }}</template>
          </el-table-column>
          <el-table-column label="支付凭证" align="center" min-width="120">
            <template slot-scope="scope">
              <lease-auth-image
                v-if="scope.row.voucherUrl"
                :key="'voucher-' + scope.row.id"
                :src="scope.row.voucherUrl"
                :width="40"
                :height="40"
              />
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="120">
            <template slot-scope="scope">
              <span
                v-if="scope.row.voucherUrl"
                class="content-edit"
                @click="onDownloadVoucher(scope.row)"
              >下载凭证</span>
              <span v-else>--</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <page-change :pageInfo="pageInfo"></page-change>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import mixins from "@/common/MixIn.js";
import PageChange from "@/components/common/PageChange.vue";
import LeaseAuthImage from "@/components/fishPondLease/common/LeaseAuthImage.vue";
import {
  pageRent,
  getRentStats,
  exportRent,
  getRentVoucherUrl,
  getHierarchyChildren,
  leaseRequestError,
  resolveLeaseResourceUrl
} from "@/service/fishPondLease";
import { enumLabel } from "@/service/fishPondLeaseEnums";

export default {
  name: "RentManage",
  mixins: [mixins],
  components: { PageChange, LeaseAuthImage },
  data() {
    return {
      list: [],
      filters: {
        lessorEnterpriseId: "",
        tenantName: ""
      },
      dateRange: [],
      companyOptions: [],
      loading: false,
      statsLoading: false,
      stats: {
        incomeTotal: 0,
        receivedTotal: 0,
        pendingTotal: 0,
        avgAmount: 0
      },
      pageInfo: {
        pageNumber: 1,
        totalPages: 0,
        pageFunc: this.search,
        pageSize: 10
      }
    };
  },
  computed: {
    isLevel2() {
      return Number(this.curCompany && this.curCompany.enterpriseType) === 2;
    }
  },
  async mounted() {
    await this.initPagePermission();
    if (this.isLevel2) {
      await this.loadCompanies();
    }
    this.search(1);
  },
  methods: {
    enumLabel,
    formatDateTime(val) {
      if (!val) return "-";
      return moment(val).format("YYYY-MM-DD HH:mm:ss");
    },
    formatMoney(val) {
      const n = Number(val);
      if (isNaN(n)) return "0.00";
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    resolveFileUrl(url) {
      return resolveLeaseResourceUrl(url);
    },
    async loadCompanies() {
      try {
        const res = await getHierarchyChildren();
        this.companyOptions = (res && res.data) || [];
      } catch (err) {
        this.$message.error(leaseRequestError(err));
      }
    },
    buildQuery() {
      const range = this.dateRange || [];
      const query = {
        tenantName: this.filters.tenantName || undefined
      };
      if (this.isLevel2) {
        query.lessorEnterpriseId = this.filters.lessorEnterpriseId || undefined;
        query.startDate = range[0] || undefined;
        query.endDate = range[1] || undefined;
      }
      return query;
    },
    loadStats() {
      if (!this.isLevel2) return;
      this.statsLoading = true;
      const params = this.buildQuery();
      // 与源二级公司页面一致：统计按出租方和支付日期筛选，不受承租方名称影响
      delete params.tenantName;
      getRentStats(params)
        .then(res => {
          this.stats = (res && res.data) || {
            incomeTotal: 0,
            receivedTotal: 0,
            pendingTotal: 0,
            avgAmount: 0
          };
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        })
        .finally(() => {
          this.statsLoading = false;
        });
    },
    search(pageNum) {
      const pageIndex = pageNum || this.pageInfo.pageNumber || 1;
      this.pageInfo.pageNumber = pageIndex;
      if (this.isLevel2) this.loadStats();
      const params = Object.assign({}, this.buildQuery(), {
        pageIndex: pageIndex,
        pageSize: this.pageInfo.pageSize
      });
      this.loading = true;
      pageRent(params)
        .then(res => {
          const data = (res && res.data) || {};
          this.list = data.list || [];
          this.pageInfo.totalPages = data.total || 0;
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetSearch() {
      this.filters = {
        lessorEnterpriseId: "",
        tenantName: ""
      };
      this.dateRange = [];
      this.search(1);
    },
    onExport() {
      const params = Object.assign({}, this.buildQuery(), {
        pageIndex: 1,
        pageSize: 100000
      });
      exportRent(params).catch(err => {
        this.$message.error(leaseRequestError(err));
      });
    },
    onDownloadVoucher(row) {
      if (row.voucherUrl) {
        window.open(this.resolveFileUrl(row.voucherUrl), "_blank");
        return;
      }
      getRentVoucherUrl(row.id)
        .then(res => {
          const data = (res && res.data) || {};
          const url = data.url || data;
          if (!url || typeof url !== "string") {
            this.$message.warning("暂无凭证");
            return;
          }
          window.open(this.resolveFileUrl(url), "_blank");
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        });
    }
  }
};
</script>

<style lang="stylus" scoped>
.rentManage .content-container .titieMian .select-container
  margin-top -5px
.stats-cards
  display flex
  gap 16px
  padding 0 0 16px
.stat-card
  flex 1
  background #fff
  border 1px solid #ebeef5
  border-radius 4px
  padding 16px 20px
.stat-label
  color #909399
  font-size 13px
  margin-bottom 8px
.stat-value
  color #315bdb
  font-size 24px
  font-weight 600
.content-edit
  color #3389ff
  cursor pointer
.lease-auth-image
  display inline-block
  vertical-align middle
</style>
